// Simple extraction of data transformation functions - no changes to logic
import React from "react";
import { Typography } from "antd";
import {
  GlobalOutlined,
  FolderOpenOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { GiBus } from "react-icons/gi";
import { TbBusStop } from "react-icons/tb";
import { IoBusinessSharp } from "react-icons/io5";
import { FaMobileAlt } from "react-icons/fa";
import { MdComputer } from "react-icons/md";
import { FaPhoneAlt } from "react-icons/fa";
import { HiOutlineTicket } from "react-icons/hi2";
import { GiBusStop } from "react-icons/gi";

const { Text } = Typography;

export const isNumeric = (v) => !isNaN(v) && !isNaN(parseFloat(v));

export const transformObjectToTree = (obj, parentKey = "") =>
  Object.entries(obj).map(([k, v]) => {
    const key = `${parentKey}-${k}`;
    if (v && typeof v === "object") {
      return {
        title: (
          <span>
            <FolderOpenOutlined
              style={{ marginRight: 6, color: "#b5b4b3" }}
            />
            {k}
          </span>
        ),
        key,
        children: transformObjectToTree(v, key),
      };
    }
    return {
      title: (
        <span>
          <FileOutlined style={{ marginRight: 6, color: "#b5b4b3" }} />
          {k}: <Text strong>{isNumeric(v) ? v : String(v)}</Text>
        </span>
      ),
      key,
      isLeaf: true,
    };
  });

export const transformDataToTree = (data) => {
  if (!data) return [];

  const nodes = [];

  // Add Global Index if available
  if (isNumeric(data["Indice Sintetico Globale"])) {
    nodes.push({
      title: (
        <span>
          <GlobalOutlined style={{ marginRight: 6, color: "#faad14" }} />
          <strong>Indice Sintetico Globale:</strong>{" "}
          {parseFloat(data["Indice Sintetico Globale"]).toFixed(2)}
        </span>
      ),
      key: "global-index",
      isLeaf: true,
    });
  }

  // Add Cities section if available
  if (data.Citta) {
    const citiesNode = {
      title: (
        <span>
          <IoBusinessSharp style={{ marginRight: 6, color: "#faad14" }} />
          Città
        </span>
      ),
      key: "cities",
      children: [],
    };

    Object.entries(data.Citta).forEach(([cityName, cityData]) => {
      const cityNode = {
        title: (
          <span>
            <IoBusinessSharp style={{ marginRight: 6, color: "#faad14" }} />
            {cityName}
          </span>
        ),
        key: `city-${cityName}`,
        children: [],
      };

      // Add City Index if available
      if (isNumeric(cityData["Indice Sintetico Citta"])) {
        cityNode.children.push({
          title: (
            <span>
              <FileOutlined style={{ marginRight: 6, color: "#b5b4b3" }} />
              <strong>Indice Sintetico Città:</strong>{" "}
              {parseFloat(cityData["Indice Sintetico Citta"]).toFixed(2)}
            </span>
          ),
          key: `city-${cityName}-index`,
          isLeaf: true,
        });
      }

      // Add Lines (URBANO/EXTRAURBANO) if available
      if (cityData.Linee) {
        const linesNode = {
          title: (
            <span>
              <GiBus style={{ marginRight: 6, color: "#52c41a" }} />
              Linee
            </span>
          ),
          key: `city-${cityName}-lines`,
          children: [],
        };

        Object.entries(cityData.Linee).forEach(([lineType, lineData]) => {
          const lineNode = {
            title: (
              <span>
                <GiBus style={{ marginRight: 6, color: "#52c41a" }} />
                {lineType}
              </span>
            ),
            key: `city-${cityName}-lines-${lineType}`,
            children: [],
          };

          // Add Line metrics
          Object.entries(lineData).forEach(([metric, value]) => {
            lineNode.children.push({
              title: (
                <span>
                  <FileOutlined
                    style={{ marginRight: 6, color: "#b5b4b3" }}
                  />
                  {metric}:{" "}
                  <Text strong>
                    {isNumeric(value)
                      ? parseFloat(value).toFixed(2)
                      : String(value)}
                  </Text>
                </span>
              ),
              key: `city-${cityName}-lines-${lineType}-${metric}`,
              isLeaf: true,
            });
          });

          linesNode.children.push(lineNode);
        });

        cityNode.children.push(linesNode);
      }

      // Add Fermate if available
      if (cityData.Fermate) {
        const fermateNode = {
          title: (
            <span>
              <GiBusStop style={{ marginRight: 6, color: "#13c2c2" }} />
              Fermate
            </span>
          ),
          key: `city-${cityName}-fermate`,
          children: [],
        };

        Object.entries(cityData.Fermate).forEach(([metric, value]) => {
          fermateNode.children.push({
            title: (
              <span>
                <FileOutlined style={{ marginRight: 6, color: "#b5b4b3" }} />
                {metric}:{" "}
                <Text strong>
                  {isNumeric(value)
                    ? parseFloat(value).toFixed(2)
                    : String(value)}
                </Text>
              </span>
            ),
            key: `city-${cityName}-fermate-${metric}`,
            isLeaf: true,
          });
        });

        cityNode.children.push(fermateNode);
      }

      // Add Punti Vendita if available
      if (cityData["Punti Vendita"]) {
        const puntiVenditaNode = {
          title: (
            <span>
              <HiOutlineTicket style={{ marginRight: 6, color: "#722ed1" }} />
              Punti Vendita
            </span>
          ),
          key: `city-${cityName}-punti-vendita`,
          children: [],
        };

        Object.entries(cityData["Punti Vendita"]).forEach(
          ([metric, value]) => {
            puntiVenditaNode.children.push({
              title: (
                <span>
                  <FileOutlined
                    style={{ marginRight: 6, color: "#b5b4b3" }}
                  />
                  {metric}:{" "}
                  <Text strong>
                    {isNumeric(value)
                      ? parseFloat(value).toFixed(2)
                      : String(value)}
                  </Text>
                </span>
              ),
              key: `city-${cityName}-punti-vendita-${metric}`,
              isLeaf: true,
            });
          }
        );

        cityNode.children.push(puntiVenditaNode);
      }

      citiesNode.children.push(cityNode);
    });

    nodes.push(citiesNode);
  }

  // Add App section if available
  if (data.App) {
    const appNode = {
      title: (
        <span>
          <FaMobileAlt style={{ marginRight: 6, color: "#faad14" }} />
          App
        </span>
      ),
      key: "app",
      children: [],
    };

    Object.entries(data.App).forEach(([metric, value]) => {
      appNode.children.push({
        title: (
          <span>
            <FileOutlined style={{ marginRight: 6, color: "#b5b4b3" }} />
            {metric}:{" "}
            <Text strong>
              {isNumeric(value)
                ? parseFloat(value).toFixed(2)
                : String(value)}
            </Text>
          </span>
        ),
        key: `app-${metric}`,
        isLeaf: true,
      });
    });

    nodes.push(appNode);
  }

  // Add Sito section if available
  if (data.Sito) {
    const sitoNode = {
      title: (
        <span>
          <MdComputer style={{ marginRight: 6, color: "#faad14" }} />
          Sito Web
        </span>
      ),
      key: "sito",
      children: [],
    };

    Object.entries(data.Sito).forEach(([metric, value]) => {
      sitoNode.children.push({
        title: (
          <span>
            <FileOutlined style={{ marginRight: 6, color: "#b5b4b3" }} />
            {metric}:{" "}
            <Text strong>
              {isNumeric(value)
                ? parseFloat(value).toFixed(2)
                : String(value)}
            </Text>
          </span>
        ),
        key: `sito-${metric}`,
        isLeaf: true,
      });
    });

    nodes.push(sitoNode);
  }

  // Add Call Center section if available
  if (data["Call Center"]) {
    const callCenterNode = {
      title: (
        <span>
          <FaPhoneAlt style={{ marginRight: 6, color: "#faad14" }} />
          Call Center
        </span>
      ),
      key: "call-center",
      children: [],
    };

    Object.entries(data["Call Center"]).forEach(([metric, value]) => {
      callCenterNode.children.push({
        title: (
          <span>
            <FileOutlined style={{ marginRight: 6, color: "#b5b4b3" }} />
            {metric}:{" "}
            <Text strong>
              {isNumeric(value)
                ? parseFloat(value).toFixed(2)
                : String(value)}
            </Text>
          </span>
        ),
        key: `call-center-${metric}`,
        isLeaf: true,
      });
    });

    nodes.push(callCenterNode);
  }

  return nodes;
};